import React, { FC, useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import type {
  Organisation,
  OrganisationMember,
  OrganisationGroup,
  MyOrganisationCardResponse,
  OrganisationCardPaymentsResponse,
  OrganisationCardPaymentDocumentsResponse,
  OrganisationCardLimitMember,
  OrganisationCardLimitMembersResponse,
  OrganisationCardPolicyOverride,
  OrganisationCardPolicy,
  OrganisationCardPolicyResponse,
  SetOrganisationCardPolicyOverrideRequest,
  UpdateOrganisationCardPolicyRequest,
} from '@dreamai/types';
import { codedErrorMessage } from './organisationCreditLabels';
import OrganisationCardPolicyForm from './OrganisationCardPolicyForm';
import OrganisationCardOverrides, {
  CardOverrideTarget,
} from './OrganisationCardOverrides';
import OrganisationCardPayments, {
  PurchaseLedgerQuery,
  currentMonthKey,
  purchaseLedgerPath,
} from './OrganisationCardPayments';
import OrganisationCardMemberLimitsModal, {
  MemberLimitChange,
} from './OrganisationCardMemberLimitsModal';
import OrganisationMyCard from './OrganisationMyCard';
import type {
  CardAssignments,
  OrganisationPaymentCard,
} from './organisationPaymentCards';

interface OrganisationCardPanelProps {
  /** Groups are switched on for this organisation (the backend lists them) */
  groupsAvailable: boolean;
  organisation: Organisation;
  members: OrganisationMember[];
  groups: OrganisationGroup[];
  isAdmin: boolean;
  /** The caller holds an active seat (of either kind) in this organisation */
  memberSeat: boolean;
  /** Saved cards and which one member purchases are charged to (admins only) */
  paymentCards?: {
    cards: OrganisationPaymentCard[];
    assignments: CardAssignments;
  };
  /** A card is being added or switched */
  cardsBusy?: boolean;
  onSetActiveCard?: (cardId: string) => Promise<boolean>;
  onAddCard?: () => void;
}

type Feedback = { type: 'success' | 'error'; message: string } | null;

function overrideKey(target: CardOverrideTarget): string {
  return target.kind === 'group'
    ? `card:group:${target.group.id}`
    : `card:member:${target.member.id}`;
}

function overridePath(base: string, target: CardOverrideTarget): string {
  return target.kind === 'group'
    ? `${base}/card/policy/groups/${target.group.id}`
    : `${base}/card/policy/members/${target.member.id}`;
}

function overrideName(target: CardOverrideTarget): string {
  return target.kind === 'group' ? target.group.name : target.member.name;
}

function memberLimitPath(
  base: string,
  member: OrganisationCardLimitMember,
): string {
  if (member.membershipId) {
    return `${base}/card/policy/members/${member.membershipId}`;
  }
  if (!member.providerRef) {
    throw new Error(`No card-limit target for ${member.name}`);
  }
  return `${base}/card/policy/members/slack/${encodeURIComponent(
    member.providerRef.teamId,
  )}/${encodeURIComponent(member.providerRef.userId)}`;
}

/**
 * Team cards. Admins pick the saved card members' purchases are charged to,
 * set the organisation-wide and per-member limits and see every purchase on
 * the Team Cards; members see only their own limits and purchases. Group
 * limits appear only while Groups are on for the organisation.
 */
const OrganisationCardPanel: FC<OrganisationCardPanelProps> = ({
  groupsAvailable,
  organisation,
  members,
  groups,
  isAdmin,
  memberSeat,
  paymentCards,
  cardsBusy = false,
  onSetActiveCard,
  onAddCard,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const base = `/organisations/${organisation.id}`;

  const [policy, setPolicy] = useState<OrganisationCardPolicy | null>(null);
  const [overrides, setOverrides] = useState<OrganisationCardPolicyOverride[]>(
    [],
  );
  const [limitMembers, setLimitMembers] =
    useState<OrganisationCardLimitMembersResponse | null>(null);
  const [ledger, setLedger] = useState<OrganisationCardPaymentsResponse | null>(
    null,
  );
  const [mine, setMine] = useState<MyOrganisationCardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [memberLimitsOpen, setMemberLimitsOpen] = useState(false);

  const authHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently]);

  const applyPolicy = (data: OrganisationCardPolicyResponse) => {
    setPolicy(data.policy);
    setOverrides(data.overrides);
  };

  const loadAdmin = useCallback(async () => {
    const headers = await authHeaders();
    const [po, pa, pm] = await Promise.all([
      api.get<OrganisationCardPolicyResponse>(`${base}/card/policy`, headers),
      api.get<OrganisationCardPaymentsResponse>(
        purchaseLedgerPath(base, {
          month: currentMonthKey(),
          membershipId: null,
        }),
        headers,
      ),
      api.get<OrganisationCardLimitMembersResponse>(
        `${base}/card/policy/members`,
        headers,
      ),
    ]);
    setPolicy(po.data.policy);
    setOverrides(po.data.overrides);
    setLedger(pa.data);
    setLimitMembers(pm.data);
  }, [authHeaders, base]);

  const fetchLedger = useCallback(
    async (query: PurchaseLedgerQuery) => {
      const { data } = await api.get<OrganisationCardPaymentsResponse>(
        purchaseLedgerPath(base, query),
        await authHeaders(),
      );
      return data;
    },
    [authHeaders, base],
  );

  const fetchDocuments = useCallback(
    async (paymentId: string) => {
      const { data } = await api.get<OrganisationCardPaymentDocumentsResponse>(
        `${base}/card/payments/${paymentId}/documents`,
        await authHeaders(),
      );
      return data;
    },
    [authHeaders, base],
  );

  const loadMine = useCallback(async () => {
    const { data } = await api.get<MyOrganisationCardResponse>(
      `${base}/card/me`,
      await authHeaders(),
    );
    setMine(data);
  }, [authHeaders, base]);

  const load = useCallback(async () => {
    try {
      if (isAdmin) await loadAdmin();
      else if (memberSeat) await loadMine();
    } catch (err) {
      console.error('Error loading organisation purchases:', err);
      setFeedback({
        type: 'error',
        message: codedErrorMessage(
          err,
          'Could not load organisation purchases.',
        ),
      });
    } finally {
      setLoading(false);
    }
  }, [isAdmin, memberSeat, loadAdmin, loadMine]);

  useEffect(() => {
    load();
  }, [load, members]);

  const run = useCallback(
    async (
      key: string,
      action: () => Promise<string | void>,
      fallback: string,
    ): Promise<boolean> => {
      setFeedback(null);
      setBusyKey(key);
      try {
        const message = await action();
        if (message) setFeedback({ type: 'success', message });
        return true;
      } catch (err) {
        console.error(fallback, err);
        setFeedback({
          type: 'error',
          message: codedErrorMessage(err, fallback),
        });
        return false;
      } finally {
        setBusyKey(null);
      }
    },
    [],
  );

  const handleSavePolicy = (patch: UpdateOrganisationCardPolicyRequest) =>
    run(
      'card:policy',
      async () => {
        const { data } = await api.patch<OrganisationCardPolicyResponse>(
          `${base}/card/policy`,
          patch,
          await authHeaders(),
        );
        applyPolicy(data);
        return 'Purchase settings saved.';
      },
      'Could not save the purchase settings.',
    );

  const handleSaveOverride = (
    target: CardOverrideTarget,
    values: SetOrganisationCardPolicyOverrideRequest,
  ) =>
    run(
      overrideKey(target),
      async () => {
        const { data } = await api.put<OrganisationCardPolicyResponse>(
          overridePath(base, target),
          values,
          await authHeaders(),
        );
        applyPolicy(data);
        return `Spending limits for ${overrideName(target)} saved.`;
      },
      'Could not save those spending limits.',
    );

  const handleClearOverride = (target: CardOverrideTarget) =>
    run(
      overrideKey(target),
      async () => {
        const { data } = await api.delete<OrganisationCardPolicyResponse>(
          overridePath(base, target),
          await authHeaders(),
        );
        applyPolicy(data);
        return `${overrideName(target)} now uses the organisation limits.`;
      },
      'Could not clear those spending limits.',
    );

  const handleSaveMemberLimits = (changes: MemberLimitChange[]) =>
    run(
      'card:members',
      async () => {
        const headers = await authHeaders();
        for (const change of changes) {
          const path = memberLimitPath(base, change.member);
          const { data } =
            change.kind === 'inherit'
              ? await api.delete<OrganisationCardPolicyResponse>(path, headers)
              : await api.put<OrganisationCardPolicyResponse>(
                  path,
                  change.values,
                  headers,
                );
          applyPolicy(data);
        }
        const { data: memberData } =
          await api.get<OrganisationCardLimitMembersResponse>(
            `${base}/card/policy/members`,
            headers,
          );
        setLimitMembers(memberData);
        return changes.length === 1
          ? `Spending limits for ${changes[0].member.name} saved.`
          : `Spending limits for ${changes.length} members saved.`;
      },
      'Could not save those spending limits.',
    );

  const renderFeedback = () =>
    feedback && (
      <p
        className={`organisation-feedback ${feedback.type}`}
        role={feedback.type === 'error' ? 'alert' : 'status'}
      >
        {feedback.message}
      </p>
    );

  if (!isAdmin && !memberSeat) return null;
  if (loading) {
    return <p className='organisation-empty'>Loading purchases…</p>;
  }

  if (!isAdmin) {
    if (!mine) return renderFeedback() || null;
    return (
      <>
        <OrganisationMyCard mine={mine} />
        {renderFeedback()}
      </>
    );
  }

  const cards = paymentCards?.cards ?? [];
  const hasCards = cards.length > 0;

  return (
    <>
      {policy && (
        <OrganisationCardPolicyForm
          policy={policy}
          busy={busyKey === 'card:policy'}
          cards={cards}
          activeCardId={paymentCards?.assignments.memberSpending ?? null}
          cardsBusy={cardsBusy}
          onSetActiveCard={onSetActiveCard ?? (async () => false)}
          onAddCard={onAddCard ?? (() => undefined)}
          onSave={handleSavePolicy}
          onAdjustMembers={() => setMemberLimitsOpen(true)}
        />
      )}
      {policy && groupsAvailable && hasCards && policy.cardEnabled && (
        <OrganisationCardOverrides
          policy={policy}
          overrides={overrides}
          groups={groups}
          busyKey={busyKey}
          onSave={handleSaveOverride}
          onClear={handleClearOverride}
        />
      )}
      {ledger && policy && (hasCards || ledger.payments.length > 0) && (
        <OrganisationCardPayments
          ledger={ledger}
          currency={policy.currency}
          members={members}
          fetchLedger={fetchLedger}
          fetchDocuments={fetchDocuments}
        />
      )}
      {renderFeedback()}
      {policy && memberLimitsOpen && (
        <OrganisationCardMemberLimitsModal
          policy={policy}
          overrides={overrides}
          personalAssistantMembers={
            limitMembers?.personalAssistantMembers ?? []
          }
          teamAssistantUsers={limitMembers?.teamAssistantUsers ?? []}
          busy={busyKey === 'card:members'}
          onClose={() => setMemberLimitsOpen(false)}
          onSave={handleSaveMemberLimits}
        />
      )}
    </>
  );
};

export default OrganisationCardPanel;
